/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './SidebarListItem';

const SidebarListItemManager: ComponentManagerFn<'SidebarListItem'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'SidebarListItem'>(platform, 'SidebarListItem'),
    type: 'SidebarListItem',
    name: 'Sidebar List Item',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      name: {
        label: 'Name',
        type: 'string',
      },
      icon: {
        label: 'Icon',
        type: 'iconselect',
      },
      iconColorExpression: {
        label: 'Icon Color',
        type: 'ColorExpression',
        placeholder: 'e.g. #a2a0a2 or blue or primary',
      },
    },
    getDisplayText: (item) => item.props.name ?? '',
    category: 'Sidebar',
    icon: { iconName: 'list' },
    description: `**Overview**<br/>
The Sidebar List Item is the individual entry component used within a SidebarList. It represents a single clickable row in a sidebar menu, typically consisting of an icon and a text label.
Use this component to define the static options available to a user in a sidebar navigation or action panel.

**1. Configuration**<br/>
* **Name:** The text label displayed for the item (e.g., "Dashboard", "Settings").
* **Icon:** The visual symbol displayed to the left of the text. You can select from the system's icon library.
* **Icon Color:** Allows you to customize the color of the icon using a ColorExpression. This supports standard color names (e.g., "blue"), hex codes (e.g., "#FF0000"), or theme variables (e.g., "primary"), enabling dynamic styling based on state (e.g., highlighting the active page).

**2. Interactivity**<br/>
* **TriggerOnClick:** The component supports the TriggerOnClick child. This is essential for making the item functional—you add this child to define what happens when the user clicks the row (e.g., navigating to a different page, opening a modal, or triggering a workflow).

**3. Best Use Cases**<br/>
* **Navigation Links:** Creating distinct "Home", "Reports", and "Profile" links in a main menu.
* **Action Buttons:** A "Log Out" button at the bottom of a sidebar.
* **Status Indicators:** Displaying a static item like "System Status: Online" where the icon color changes based on an expression.`,
  };
};

export default SidebarListItemManager;
